import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, CircularProgress,
  Modal, Stepper, Step, StepLabel,
  Radio, RadioGroup, FormControlLabel, FormControl,
  FormLabel, Checkbox, FormGroup, Chip,
  Drawer
} from '@mui/material';
import { IconLocation } from '../BinkoIcons';
import api from '../services/api';
import { useNav } from '../App';
import { initPushNotifications } from '../firebase';
import { C } from '../theme';

const S = {
  root: { minHeight: '100vh', background: C.bgApp, padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '8px' },
  title: { fontSize: '22px', fontWeight: 500, color: C.textPrimary, letterSpacing: '-0.4px' },
  ticker: { background: C.bgCard, border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: '10px', padding: '10px 14px', fontSize: '12px', color: C.textMuted, display: 'flex', alignItems: 'center', gap: '8px' },
  tickerDot: { width: '6px', height: '6px', borderRadius: '50%', background: C.teal, flexShrink: 0 },
  ctaCard: { background: C.bgCard, border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: '16px', padding: '16px', cursor: 'pointer', transition: 'border-color 0.15s' },
  ctaTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  ctaIcon: { fontSize: '28px', lineHeight: 1 },
  ctaMeta: { fontSize: '11px', color: C.textMuted, textAlign: 'right' },
  ctaTitle: { fontSize: '16px', fontWeight: 500, color: C.textPrimary, margin: '8px 0 4px' },
  ctaSub: { fontSize: '12px', color: C.textMuted, lineHeight: 1.4 },
  ghostBtn: { border: '0.5px solid rgba(255,255,255,0.07)', background: 'transparent', color: C.textMuted, borderRadius: '10px', padding: '10px 16px', fontSize: '13px', cursor: 'pointer', width: '100%', marginTop: '4px' },
  venuePill: { display: 'flex', alignItems: 'center', gap: '5px', background: 'rgba(13,157,165,0.12)', border: '0.5px solid rgba(13,157,165,0.25)', borderRadius: '20px', padding: '4px 10px', fontSize: '11px', color: C.teal },
  sectionLabel: { fontSize: '11px', color: C.textDisabled, letterSpacing: '0.8px', textTransform: 'uppercase', padding: '4px 0 2px' },
  venueRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: C.bgCard, border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: '12px' },
  venueNameText: { fontSize: '14px', color: C.textPrimary, fontWeight: 500 },
  venueArea: { fontSize: '11px', color: C.textMuted, marginTop: '2px' },
  venueCount: { fontSize: '11px', color: C.teal },
  checkInBtn: { background: 'rgba(13,157,165,0.12)', border: '0.5px solid rgba(13,157,165,0.3)', color: C.teal, borderRadius: '8px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' },
  primaryBtn: { background: C.yellow, color: C.yellowText, border: 'none', borderRadius: '12px', padding: '13px 20px', fontSize: '14px', fontWeight: 500, cursor: 'pointer', width: '100%' },
  nciBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 0 16px', gap: '4px' },
};

function formatAgeBand(dob) {
  if (!dob) return '';
  const age = Math.floor((Date.now() - new Date(dob).getTime()) / (365.25 * 24 * 3600 * 1000));
  const lower = Math.max(21, age - 2);
  const upper = age + 1;
  return `${lower}–${upper}`;
}

function TickerBar({ liveStats }) {
  const [idx, setIdx]       = useState(0);
  const [visible, setVisible] = useState(true);

  const msgs = [];
  if (liveStats) {
    if (liveStats.checkinsToday > 0)
      msgs.push(`${liveStats.checkinsToday} people checked in today`);
    if (liveStats.triviaPlayers > 0)
      msgs.push(`${liveStats.triviaPlayers} people played trivia today`);
    if (liveStats.wavesToday > 0)
      msgs.push(`${liveStats.wavesToday} waves sent today`);
    if (liveStats.scoresOver110 > 0)
      msgs.push(`${liveStats.scoresOver110} scores crossed 110 today`);
    if (liveStats.topScorer)
      msgs.push(`Top scorer: ${liveStats.topScorer.alias} — ${liveStats.topScorer.score} pts`);
    if (liveStats.nextTournamentTime)
      msgs.push(`Tournament starts at ${liveStats.nextTournamentTime} today`);
  }

  // Every 4th message use the mechanic anchor — splice it in
  const finalMsgs = msgs.length > 1
    ? msgs.reduce((acc, m, i) => { acc.push(m); if ((i + 1) % 3 === 0) acc.push('Play trivia · meet people between rounds'); return acc; }, [])
    : [...msgs, 'Play trivia · meet people between rounds'];

  useEffect(() => {
    if (finalMsgs.length <= 1) return;
    const t = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx(i => (i + 1) % finalMsgs.length);
        setVisible(true);
      }, 400);
    }, 4500);
    return () => clearInterval(t);
  }, [finalMsgs.length]);

  return (
    <Box sx={{
      background: 'rgba(13,157,165,0.05)',
      border: '0.5px solid rgba(13,157,165,0.14)',
      borderRadius: '10px',
      padding: '10px 14px',
      display: 'flex', alignItems: 'center', gap: '10px',
      minHeight: '40px', overflow: 'hidden',
    }}>

      <Typography sx={{
        fontSize: '12px', color: '#9ca3af',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(-10px)',
        transition: 'opacity 0.4s, transform 0.4s',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {finalMsgs[idx]}
      </Typography>
    </Box>
  );
}

const POSITIVE_REASONS = [
  { value: 'met_someone',     label: '🤝 Met someone interesting' },
  { value: 'fun_trivia',      label: '🎮 Loved the trivia' },
  { value: 'easy_to_use',     label: '✨ Super easy to use' },
  { value: 'good_vibe',       label: '🍻 Great venue vibe' },
];

function AppFbPositive({ onSubmit, onSkip }) {
  const [selected, setSelected] = useState(null);
  return (
    <Box>
      <Typography sx={{ fontSize: '17px', fontWeight: 600, color: C.textPrimary, mb: '4px' }}>
        What did you enjoy? 👍
      </Typography>
      <Typography sx={{ fontSize: '13px', color: C.textMuted, mb: '16px' }}>
        Takes 2 seconds — helps us build the right things.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {POSITIVE_REASONS.map(r => (
          <Box key={r.value}
            onClick={() => setSelected(r.value)}
            sx={{
              px: '14px', py: '12px', borderRadius: '10px', cursor: 'pointer',
              border: selected === r.value
                ? '1.5px solid rgba(13,157,165,0.7)'
                : '0.5px solid rgba(255,255,255,0.08)',
              background: selected === r.value ? 'rgba(13,157,165,0.08)' : C.bgElevated,
              transition: 'all 0.15s',
            }}>
            <Typography sx={{ fontSize: '14px', color: selected === r.value ? C.teal : C.textPrimary }}>
              {r.label}
            </Typography>
          </Box>
        ))}
      </Box>
      <Button fullWidth disabled={!selected} onClick={() => onSubmit(selected)}
        sx={{
          mt: '20px', background: C.teal, color: '#fff', fontWeight: 700,
          borderRadius: '12px', height: '48px', textTransform: 'none', fontSize: '14px',
          '&:hover': { background: C.teal }, '&.Mui-disabled': { opacity: 0.35 },
        }}>
        Submit
      </Button>
      <Button fullWidth onClick={onSkip}
        sx={{ mt: '8px', color: C.textMuted, fontSize: '13px', textTransform: 'none' }}>
        Skip
      </Button>
    </Box>
  );
}

const NEGATIVE_REASONS = [
  { value: 'hard_to_use',      label: '😕 Hard to figure out' },
  { value: 'no_one_here',      label: '🏜 Not enough people' },
  { value: 'match_quality',    label: '💔 Match quality was off' },
  { value: 'technical_issues', label: '🐛 Technical issues' },
  { value: 'missing_feature',  label: '🔧 Missing a feature I wanted' },
];

function AppFbNegative({ onSubmit, onSkip }) {
  const [selected, setSelected] = useState(null);
  return (
    <Box>
      <Typography sx={{ fontSize: '17px', fontWeight: 600, color: C.textPrimary, mb: '4px' }}>
        What wasn’t working? 👎
      </Typography>
      <Typography sx={{ fontSize: '13px', color: C.textMuted, mb: '16px' }}>
        We read every response. Help us fix it.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {NEGATIVE_REASONS.map(r => (
          <Box key={r.value}
            onClick={() => setSelected(r.value)}
            sx={{
              px: '14px', py: '12px', borderRadius: '10px', cursor: 'pointer',
              border: selected === r.value
                ? '1.5px solid rgba(251,188,21,0.6)'
                : '0.5px solid rgba(255,255,255,0.08)',
              background: selected === r.value ? 'rgba(251,188,21,0.07)' : C.bgElevated,
              transition: 'all 0.15s',
            }}>
            <Typography sx={{ fontSize: '14px', color: selected === r.value ? C.yellow : C.textPrimary }}>
              {r.label}
            </Typography>
          </Box>
        ))}
      </Box>
      <Button fullWidth disabled={!selected} onClick={() => onSubmit(selected)}
        sx={{
          mt: '20px', background: C.yellow, color: C.yellowText, fontWeight: 700,
          borderRadius: '12px', height: '48px', textTransform: 'none', fontSize: '14px',
          '&:hover': { background: C.yellow }, '&.Mui-disabled': { opacity: 0.35 },
        }}>
        Submit
      </Button>
      <Button fullWidth onClick={onSkip}
        sx={{ mt: '8px', color: C.textMuted, fontSize: '13px', textTransform: 'none' }}>
        Skip
      </Button>
    </Box>
  );
}

export default function Home() {
  const navigate = useNav();
  const [user, setUser]           = useState(null);
  const [checkin, setCheckin]     = useState(null);
  const [venues, setVenues]       = useState([]);
  const [allVenues, setAllVenues]   = useState([]);
  const [sheetOpen, setSheetOpen]       = useState(false);
  const [sheetState, setSheetState]     = useState('s1');
  const [sheetVenues, setSheetVenues]   = useState([]);
  const [sheetAreas, setSheetAreas]     = useState({});
  const [sheetSelectedArea, setSheetSelectedArea] = useState('');
  const sheetDragY = useRef(0);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [oracleOpen, setOracleOpen] = useState(false);
  const [oracleText, setOracleText] = useState('');
  const [locationState, setLocationState] = useState('idle');
  const [areas, setAreas] = useState({});
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedArea, setSelectedArea] = useState('');
  const [gpsOnly, setGpsOnly] = useState(false);
  const [wasKicked, setWasKicked]       = useState(false);
  const [pendingCheckin, setPendingCheckin] = useState(false);
  const [approvedFlash, setApprovedFlash]   = useState(false);
  const [offers,       setOffers]       = useState([]);
  const [winnerCodes,  setWinnerCodes]  = useState([]);
  const [carouselIdx,  setCarouselIdx]  = useState(0);
  const [nciCarouselIdx, setNciCarouselIdx] = useState(0);
  const nciCarouselTouchX = useRef(0);
  const [walletOpen,   setWalletOpen]   = useState(false);
  const [liveStats,    setLiveStats]    = useState(null);
  const carouselTouchStartX = useRef(0);
  const [showNotifPrompt, setShowNotifPrompt] = useState(
    () => localStorage.getItem('binko_notif_dismissed') !== '1' &&
          typeof Notification !== 'undefined' &&
          Notification.permission === 'default'
  );

  // App feedback state
  const [fbTapped, setFbTapped]         = useState(false);
  const [fbDrawerOpen, setFbDrawerOpen] = useState(false);
  const [fbRating, setFbRating]         = useState(null);
  const [fbDone, setFbDone]             = useState(false);

  async function handleOracle() {
    const today = new Date().toISOString().slice(0, 10);
    const cacheKey = 'oracle_cache_' + today;
    const cached = localStorage.getItem(cacheKey);
    if (cached) { setOracleText(cached); setOracleOpen(true); return; }
    try {
      const r = await api.get('/oracle');
      const oracles = r.data.oracles;
      if (!oracles || !oracles.length) return;
      let order = JSON.parse(localStorage.getItem('oracle_order') || 'null');
      let pos = parseInt(localStorage.getItem('oracle_pos') || '0', 10);
      if (!order || order.length !== oracles.length) {
        order = oracles.map((_, i) => i).sort(() => Math.random() - 0.5);
        pos = 0;
      }
      const text = oracles[order[pos]];
      const nextPos = (pos + 1) % oracles.length;
      if (nextPos === 0) {
        order = oracles.map((_, i) => i).sort(() => Math.random() - 0.5);
      }
      localStorage.setItem('oracle_order', JSON.stringify(order));
      localStorage.setItem('oracle_pos', String(nextPos));
      localStorage.setItem(cacheKey, text);
      setOracleText(text);
      setOracleOpen(true);
    } catch (_) {}
  }

  // Check-in modal state
  const [modalOpen, setModalOpen]       = useState(false);
  const [modalVenue, setModalVenue]     = useState(null);
  const [modalStep, setModalStep]       = useState(0);
  const [isFirstTime, setIsFirstTime]   = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [modalError, setModalError]     = useState('');

  // Form fields — profile identifiers (first time only)
  const [hairType, setHairType]         = useState('');
  const [facialHair, setFacialHair]     = useState('None');
  const [hasGlasses, setHasGlasses]     = useState(false);
  // Form fields — session identifiers
  const [wearingGlasses, setWearingGlasses] = useState(false);
  const [locationArea, setLocationArea]     = useState('');
  const [clothingPrimary, setClothingPrimary]   = useState('');
  const [clothingSecondary, setClothingSecondary] = useState('');
  const [accessories, setAccessories]   = useState([]);
  const [groupStatus, setGroupStatus]   = useState('');

  // Haversine distance in metres
  function haversineM(lat1, lon1, lat2, lon2) {
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2)**2 +
              Math.cos(lat1 * Math.PI/180) * Math.cos(lat2 * Math.PI/180) *
              Math.sin(dLon/2)**2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  const load = useCallback(async () => {
    try {
      const [meRes, checkinRes, configRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/checkin/my-checkin'),
        fetch((process.env.REACT_APP_API_BASE || 'https://api.binko.me/api') + '/config')
          .then(r => r.json()).catch(() => ({ config: {} })),
      ]);
      setUser(meRes.data.user);
      setGpsOnly(configRes.config?.gps_only === '1' || configRes.config?.gps_only === true);
      if (checkinRes.data.checkedIn) {
        setCheckin(checkinRes.data.checkin);
        setLiveStats(checkinRes.data.liveStats || null);
        setWasKicked(false);
        setPendingCheckin(false);
        const venueId = checkinRes.data.checkin?.venue_id;
        if (venueId) {
          api.get('/venue/offers', { params: { venue_id: venueId } })
            .then(or => setOffers(or.data?.offers || [])).catch(() => {});
          api.get('/checkin/my-winner-codes')
            .then(wr => setWinnerCodes(wr.data?.codes || [])).catch(() => {});
        }
      } else {
        setCheckin(null);
        setLocationState('idle');
        if (checkinRes.data.wasKicked) setWasKicked(true);
        setPendingCheckin(!!checkinRes.data.pendingCheckin);
        setLiveStats(checkinRes.data.liveStats || null);
      }
    } catch (e) {
      setError('Could not load. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, []);

  async function handleRequestGPS() {
    if (!navigator.geolocation) {
      setLocationState('denied');
      return;
    }
    setLocationState('requesting');
    try {
      const venueRes = await api.get('/venues');
      const rawVenues = venueRes.data.venues || [];
      const radiusM = 300;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const nearby = rawVenues
            .filter(v => v.latitude && v.longitude)
            .map(v => ({ ...v, _dist: haversineM(pos.coords.latitude, pos.coords.longitude, v.latitude, v.longitude) }))
            .filter(v => v._dist <= radiusM)
            .sort((a, b) => a._dist - b._dist);
          setVenues(nearby);
          setLocationState('granted');
        },
        () => {
          setLocationState('denied');
        },
        { timeout: 8000, maximumAge: 0 }
      );
    } catch (e) {
      setLocationState('denied');
    }
  }

  async function handleAreaSelect(city, area) {
    setSelectedCity(city);
    setSelectedArea(area);
    try {
      const venueRes = await api.get('/venues');
      const allVenues = venueRes.data.venues || [];
      const filtered = allVenues.filter(v => v.city === city && v.area === area);
      setVenues(filtered);
      setLocationState('manual_ready');
    } catch (e) {
      setError('Could not load venues.');
    }
  }

  async function handleManualPick() {
    try {
      const res = await fetch((process.env.REACT_APP_API_BASE || 'https://api.binko.me/api') + '/venues/areas');
      const data = await res.json();
      setAreas(data.grouped || {});
      setLocationState('manual_pick');
    } catch (e) {
      setError('Could not load areas.');
    }
  }


  async function handleSheetGPS() {
    if (!navigator.geolocation) { setSheetState('s4'); return; }
    setSheetState('s2');
    try {
      const venueRes = await api.get('/venues');
      const rawVenues = venueRes.data.venues || [];
      const radiusM = 300;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const nearby = rawVenues
            .filter(v => v.latitude && v.longitude)
            .map(v => ({ ...v, _dist: haversineM(pos.coords.latitude, pos.coords.longitude, v.latitude, v.longitude) }))
            .filter(v => v._dist <= radiusM)
            .sort((a, b) => a._dist - b._dist);
          setSheetVenues(nearby);
          setSheetState(nearby.length > 0 ? 's3' : 's4');
        },
        () => { setSheetState('s4'); },
        { timeout: 8000, maximumAge: 0 }
      );
    } catch (e) { setSheetState('s4'); }
  }

  async function handleSheetManual() {
    try {
      const res = await fetch((process.env.REACT_APP_API_BASE || 'https://api.binko.me/api') + '/venues/areas');
      const data = await res.json();
      setSheetAreas(data.grouped || {});
      setSheetState('s5');
    } catch (e) { setSheetAreas({}); setSheetState('s5'); }
  }

  async function handleSheetAreaSelect(city, area, label) {
    setSheetSelectedArea(label || area);
    try {
      const venueRes = await api.get('/venues');
      const allV = venueRes.data.venues || [];
      const filtered = allV.filter(v => v.city === city && v.area === area);
      setSheetVenues(filtered);
      setSheetState('s6');
    } catch (e) { setSheetVenues([]); setSheetState('s6'); }
  }

  useEffect(() => { load(); }, [load]);
  // Silently register FCM token on mount — no prompt, no blocking
  useEffect(() => {
    const apiBase = process.env.REACT_APP_API_BASE || 'https://api.binko.me/api';
    const token = localStorage.getItem('binko_token');
    if (token) initPushNotifications(apiBase, token);
  }, []);

  // Load all venues for browse-only home list
  useEffect(() => {
    api.get('/venues').then(r => setAllVenues(r.data.venues || [])).catch(() => {});
  }, []);

  // Carousel auto-rotate (checked-in offers carousel)
  useEffect(() => {
    if (offers.length === 0) return;
    const t = setInterval(() => setCarouselIdx(i => (i + 1) % 3), 3400);
    return () => clearInterval(t);
  }, [offers]);

  // NCI carousel auto-advance
  useEffect(() => {
    const t = setInterval(() => setNciCarouselIdx(i => (i + 1) % 3), 3500);
    return () => clearInterval(t);
  }, []);

  // Feedback strip visibility
  const showFeedbackStrip = (() => {
    if (!user) return false;
    const now = Date.now();
    const submittedAt = user.app_feedback_submitted_at
      ? new Date(user.app_feedback_submitted_at) : null;
    const skippedAt = user.app_feedback_skipped_at
      ? new Date(user.app_feedback_skipped_at) : null;
    const ignoredRaw = localStorage.getItem('binko_fb_ignored_at');
    const ignoredAt  = ignoredRaw ? new Date(ignoredRaw) : null;
    return (
      (!submittedAt || submittedAt < new Date(now - 28 * 86400000)) &&
      (!skippedAt   || skippedAt   < new Date(now - 14 * 86400000)) &&
      (!ignoredAt   || ignoredAt   < new Date(now -  3 * 86400000))
    );
  })();

  useEffect(() => {
    return () => {
      if (showFeedbackStrip && !fbTapped) {
        localStorage.setItem('binko_fb_ignored_at', new Date().toISOString());
      }
    };
  }, [showFeedbackStrip, fbTapped]);

  useEffect(() => {
    if (!pendingCheckin) return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get('/checkin/my-checkin');
        if (res.data.checkedIn) {
          setPendingCheckin(false);
          setApprovedFlash(true);
          setTimeout(async () => { setApprovedFlash(false); await load(); }, 2000);
        } else if (!res.data.pendingCheckin) {
          setPendingCheckin(false);
        }
      } catch (_) {}
    }, 5000);
    return () => clearInterval(interval);
  }, [pendingCheckin, load]);

  function openCheckInModal(venue) {
    setModalVenue(venue);
    setModalError('');
    const firstTime = !user?.hair_type;
    setIsFirstTime(firstTime);
    setModalStep(firstTime ? 0 : 1);
    setModalOpen(true);
  }

  function toggleAccessory(val) {
    setAccessories(a => a.includes(val) ? a.filter(x => x !== val) : [...a, val]);
  }

  async function submitCheckIn() {
    setSubmitting(true);
    setModalError('');
    try {
      if (isFirstTime) {
        await api.post('/auth/update-identifiers', { hairType, facialHair, hasGlasses });
      }
      const ciRes = await api.post('/checkin', {
        venueId: modalVenue.id,
        wearingGlasses,
        locationArea,
        clothingPrimary,
        clothingSecondary,
        accessories: accessories.join(','),
        groupStatus,
      });
      setModalOpen(false);
      if (ciRes.data.checkinStatus === 'pending') {
        setPendingCheckin(true);
      } else {
        await load();
      }
    } catch (e) {
      const msg = e.response?.data?.message || 'Check-in failed. Please try again.';
      setModalError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCheckOut() {
    try {
      await api.post('/checkout', { venue_id: checkin?.venue_id });
      setCheckin(null);
      await load();
    } catch (_) {}
  }

  async function handleFbSubmit(reason) {
    try {
      await api.post('/feedback/app', { rating: fbRating, reason: reason || null, trigger: 'strip' });
      localStorage.removeItem('binko_fb_ignored_at');
      await load();
      setFbDone(true);
      setTimeout(() => setFbDrawerOpen(false), 1500);
    } catch (_) {}
  }

  async function handleFbSkip() {
    try {
      await api.post('/feedback/app', { skipped: true });
    } catch (_) {}
    setFbDrawerOpen(false);
  }

  if (loading) return (
    <Box sx={{ ...S.root, alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress />
    </Box>
  );

  if (error) return (
    <Box sx={{ ...S.root, alignItems: 'center', justifyContent: 'center' }}>
      <Typography sx={{ color: C.orange }}>{error}</Typography>
    </Box>
  );

  if (approvedFlash) return (
    <Box sx={{ ...S.root, alignItems: 'center', justifyContent: 'center', px: '24px' }}>
      <Box sx={{
        background: C.bgCard, border: `0.5px solid ${C.border}`,
        borderRadius: '20px', p: '32px 24px', textAlign: 'center', maxWidth: '360px',
      }}>
        <Typography sx={{ fontSize: '40px', mb: '16px' }}>✅</Typography>
        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: C.textPrimary, mb: '8px' }}>
          You're in!
        </Typography>
        <Typography sx={{ fontSize: '14px', color: C.textMuted }}>
          The venue has approved your check-in.
        </Typography>
      </Box>
    </Box>
  );

  if (pendingCheckin) return (
    <Box sx={{ ...S.root, alignItems: 'center', justifyContent: 'center', px: '24px' }}>
      <Box sx={{
        background: C.bgCard, border: `0.5px solid ${C.border}`,
        borderRadius: '20px', p: '28px 24px', textAlign: 'center', maxWidth: '360px',
      }}>
        <CircularProgress size={32} sx={{ color: C.teal, mb: '20px' }} />
        <Typography sx={{ fontSize: '17px', fontWeight: 700, color: C.textPrimary, mb: '12px' }}>
          Waiting for approval
        </Typography>
        <Typography sx={{ fontSize: '14px', color: C.textMuted, lineHeight: 1.6, mb: '8px' }}>
          Your check-in request has been sent to the venue.
        </Typography>
        <Typography sx={{ fontSize: '14px', color: C.textMuted, lineHeight: 1.6, mb: '24px' }}>
          The venue admin will approve or deny shortly.
        </Typography>
        <Typography sx={{ fontSize: '12px', color: C.textDisabled }}>
          Request expires in 10 minutes if not reviewed.
        </Typography>
      </Box>
    </Box>
  );

  if (wasKicked) return (
    <Box sx={{ ...S.root, alignItems: 'center', justifyContent: 'center', px: '24px' }}>
      <Box sx={{
        background: C.bgCard, border: `0.5px solid ${C.border}`,
        borderRadius: '20px', p: '28px 24px', textAlign: 'center', maxWidth: '360px',
      }}>
        <Typography sx={{ fontSize: '32px', mb: '12px' }}>⚠️</Typography>
        <Typography sx={{ fontSize: '17px', fontWeight: 700, color: C.textPrimary, mb: '12px' }}>
          Session Ended
        </Typography>
        <Typography sx={{ fontSize: '14px', color: C.textMuted, lineHeight: 1.6, mb: '24px' }}>
          You have been removed by the venue admin for not following community guidelines. You can check with the venue once if there has been a mistake.
        </Typography>
        <Button
          onClick={() => setWasKicked(false)}
          sx={{
            background: C.yellow, color: '#000', fontWeight: 700, borderRadius: '12px',
            px: '24px', py: '10px', textTransform: 'none', fontSize: '14px',
            '&:hover': { background: '#e0a812' },
          }}
        >
          Got it
        </Button>
      </Box>
    </Box>
  );

  const ageBand = formatAgeBand(user?.date_of_birth);

  return (
    <Box sx={S.root}>
      {/* Header — logo + avatar only */}
      <Box sx={S.header}>
        <img src="/logo.png" alt="binko" style={{ height: '32px', width: 'auto' }} />
        {user?.alias && (
          <Box
            onClick={() => navigate('/profile')}
            sx={{
              width: '32px', height: '32px', borderRadius: '50%',
              background: 'rgba(13,157,165,0.15)',
              border: '0.5px solid rgba(13,157,165,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '13px', fontWeight: 700, color: C.teal,
              cursor: 'pointer', flexShrink: 0,
            }}>
            {user.alias[0].toUpperCase()}
          </Box>
        )}
      </Box>



      {checkin ? (
        /* ── CHECKED IN STATE ── */
        <>
          {/* Greeting */}
          <Box sx={{ mb: '2px' }}>
            <Typography sx={{ fontSize: '22px', fontWeight: 700, color: C.textPrimary, mb: '3px', lineHeight: 1.2 }}>
              Hey {user?.alias} 👋
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
              Checked in to <Box component="span" sx={{ color: C.teal, fontWeight: 500 }}>{checkin.venue_name}</Box>
            </Typography>
          </Box>

          {/* Ticker */}
          <TickerBar liveStats={liveStats} />

          {/* Primary CTA — Join the Room */}
          <Box sx={{
            background: 'rgba(13,157,165,0.07)',
            border: '1px solid rgba(13,157,165,0.28)',
            borderRadius: '16px', padding: '18px 16px 16px',
            cursor: 'pointer',
          }} onClick={() => navigate('/game')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '10px' }}>
              <Box sx={{
                width: '6px', height: '6px', borderRadius: '50%', background: C.teal,
                '@keyframes livepulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
                animation: 'livepulse 1s infinite', flexShrink: 0,
              }} />
              <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.teal }}>
                Live now
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: C.textPrimary, lineHeight: 1.3, mb: '12px' }}>
              Play. Meet. Repeat.
            </Typography>
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              background: C.teal, borderRadius: '10px', padding: '10px 18px',
              fontSize: '14px', fontWeight: 700, color: '#fff',
            }}>
              Join the Room →
            </Box>
          </Box>

          {/* Secondary CTA — Meet the Room */}
          <Box sx={{
            background: 'rgba(255,255,255,0.02)',
            border: '0.5px solid rgba(255,255,255,0.07)',
            borderRadius: '14px', padding: '13px 15px',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '12px',
          }} onClick={() => navigate('/inhere')}>
            <Box sx={{
              width: '34px', height: '34px', borderRadius: '9px',
              background: 'rgba(255,255,255,0.04)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '15px', fontWeight: 600, color: C.textPrimary, mb: '2px' }}>Meet the Room</Typography>
              <Typography sx={{ fontSize: '11px', color: '#6b7280' }}>Browse people here tonight · Send a wave</Typography>
            </Box>
            <Typography sx={{ marginLeft: 'auto', color: '#374151', fontSize: '18px', flexShrink: 0 }}>›</Typography>
          </Box>



          {/* Offers carousel — modular, driven by venue_offers */}
          {(() => {
            const venueName = checkin.venue_name || 'this venue';
            const offerSlides = offers
              .filter(o => o.is_active !== false)
              .sort((a, b) => (a.sort_order || 99) - (b.sort_order || 99))
              .map(o => ({
                isTournament: o.offer_type === 'tournament',
                labelColor: o.offer_type === 'tournament' ? C.yellow : C.teal,
                strokeColor: o.offer_type === 'tournament' ? C.yellow : C.teal,
                label: o.offer_type === 'tournament' ? 'Tournament winner gets' : 'Trivia winner gets',
                offerText: o.offer_text,
                detail: o.offer_type === 'tournament'
                  ? "Tonight's champion · Claim before midnight"
                  : 'Top scorer tonight · Claim at the bar',
              }));
            if (offerSlides.length === 0) return null;
            const slides = [...offerSlides, { isTC: true }];
            const handleTouchStart = (e) => { carouselTouchStartX.current = e.touches[0].clientX; };
            const handleTouchEnd = (e) => {
              const dx = e.changedTouches[0].clientX - carouselTouchStartX.current;
              if (Math.abs(dx) < 40) return;
              if (dx < 0) setCarouselIdx(i => Math.min(i + 1, slides.length - 1));
              else        setCarouselIdx(i => Math.max(i - 1, 0));
            };
            return (
              <Box>
                <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#374151', mb: '8px' }}>
                  Venue Perks
                </Typography>
                <Box sx={{ background: 'rgba(255,255,255,0.02)', border: '0.5px solid rgba(251,188,21,0.2)', borderRadius: '14px', overflow: 'hidden' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '0.5px solid rgba(255,255,255,0.04)' }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: C.yellow }}>
                      Offers from {venueName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                      {slides.map((_, i) => (
                        <Box key={i} onClick={() => setCarouselIdx(i)} sx={{
                          width: i === carouselIdx ? '16px' : '6px', height: '6px',
                          borderRadius: i === carouselIdx ? '3px' : '50%',
                          background: i === carouselIdx ? C.yellow : 'rgba(255,255,255,0.1)',
                          cursor: 'pointer', transition: 'all 0.3s',
                        }} />
                      ))}
                    </Box>
                  </Box>
                  <Box onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} sx={{ overflow: 'hidden' }}>
                    <Box sx={{ display: 'flex', transform: `translateX(-${carouselIdx * 100}%)`, transition: 'transform 0.38s cubic-bezier(0.4,0,0.2,1)' }}>
                      {slides.map((slide, i) => (
                        <Box key={i} sx={{ minWidth: '100%', padding: '14px 15px', height: '96px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', userSelect: 'none' }}>
                          {slide.isTC ? (
                            <>
                              <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#4b5563', mb: '7px' }}>Offer terms</Typography>
                              <Typography sx={{ fontSize: '11px', color: '#4b5563', lineHeight: 1.65 }}>
                                Offers are provided directly by <Box component="span" sx={{ color: '#6b7280', fontWeight: 600 }}>{venueName}</Box>. Binko facilitates the connection — fulfilment and redemption are the sole responsibility of the venue. T&C apply.
                              </Typography>
                            </>
                          ) : (
                            <>
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', mb: '4px' }}>
                                  {slide.isTournament ? (
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={slide.strokeColor} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M6 9H4a2 2 0 0 1-2-2V5h4"/><path d="M18 9h2a2 2 0 0 0 2-2V5h-4"/>
                                      <path d="M6 9c0 4 2.5 7 6 8 3.5-1 6-4 6-8V3H6v6z"/>
                                      <path d="M9 21h6"/><path d="M12 17v4"/>
                                    </svg>
                                  ) : (
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={slide.strokeColor} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                                    </svg>
                                  )}
                                  <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: slide.labelColor }}>{slide.label}</Typography>
                                </Box>
                                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: C.textPrimary, lineHeight: 1.3 }}>{slide.offerText}</Typography>
                                <Typography sx={{ fontSize: '11px', color: '#6b7280', mt: '2px' }}>{slide.detail}</Typography>
                              </Box>
                              <Typography sx={{ fontSize: '10px', color: '#374151' }}>
                                Offer by {venueName} · <Box component="span" sx={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => setCarouselIdx(slides.length - 1)}>T&C apply</Box>
                              </Typography>
                            </>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })()}

          {/* Wins wallet */}
          {winnerCodes.filter(c => c.status !== 'expired').length > 0 && (() => {
            const activeCodes = winnerCodes.filter(c => c.status !== 'expired');
            const winCount = activeCodes.length;
            return (
              <Box sx={{ background: 'rgba(255,255,255,0.02)', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: '14px', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', cursor: 'pointer' }} onClick={() => setWalletOpen(o => !o)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
                    </svg>
                    My wins tonight
                  </Box>
                  <Box sx={{ fontSize: '11px', fontWeight: 700, color: C.yellow, background: 'rgba(251,188,21,0.1)', borderRadius: '20px', padding: '2px 9px' }}>
                    {winCount} {winCount === 1 ? 'win' : 'wins'}
                  </Box>
                </Box>
                {walletOpen && (
                  <Box sx={{ px: '14px', py: '10px' }}>
                    {activeCodes.map((wc, i) => (
                      <Box key={wc.id} sx={{ display: 'flex', alignItems: 'center', gap: '10px', py: '8px', borderBottom: i < activeCodes.length - 1 ? '0.5px solid rgba(255,255,255,0.04)' : 'none' }}>
                        <Box sx={{ width: 30, height: 30, borderRadius: '8px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: wc.game_type === 'trivia' ? 'rgba(13,157,165,0.12)' : 'rgba(251,188,21,0.12)' }}>
                          {wc.game_type === 'trivia'
                            ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={C.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                            : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={C.yellow} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4a2 2 0 0 1-2-2V5h4"/><path d="M18 9h2a2 2 0 0 0 2-2V5h-4"/><path d="M6 9c0 4 2.5 7 6 8 3.5-1 6-4 6-8V3H6v6z"/><path d="M9 21h6"/><path d="M12 17v4"/></svg>
                          }
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: wc.game_type === 'trivia' ? C.teal : C.yellow }}>
                            {wc.game_type === 'trivia' ? 'Trivia win' : 'Tournament win'}
                          </Typography>
                          <Typography sx={{ fontSize: '12px', color: C.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {wc.offer_text || 'Winner offer'}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Typography sx={{ fontSize: '16px', fontWeight: 800, color: C.yellow, letterSpacing: '0.14em' }}>
                            {wc.code.split('').join(' ')}
                          </Typography>
                          <Box sx={{ fontSize: '10px', fontWeight: 600, borderRadius: '4px', padding: '2px 6px', display: 'inline-block', mt: '2px', background: wc.status === 'redeemed' ? 'rgba(255,255,255,0.05)' : 'rgba(13,157,165,0.1)', color: wc.status === 'redeemed' ? C.textDisabled : C.teal }}>
                            {wc.status === 'redeemed' ? 'Redeemed' : 'Pending'}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })()}

          <Button sx={S.ghostBtn} onClick={handleCheckOut}>Check Out</Button>

          <Box sx={{ display: 'flex', gap: '8px', mt: '4px' }}>
            <Button sx={{ ...S.ghostBtn, margin: 0, flex: 1, color: C.yellow }} onClick={handleOracle}>Pulse ✨</Button>
            <Button sx={{ ...S.ghostBtn, margin: 0, flex: 1 }} onClick={() => window.open('https://www.youtube.com/watch?v=Z_Cql_83y8k', '_blank')}>How it works ▶</Button>
          </Box>
        </>
      ) : (
        /* ── NOT CHECKED IN STATE ── */
        <>
          {/* Salutation */}
          <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#efefef', mb: '1px' }}>
            Hey {user?.alias} 👋
          </Typography>
          <Typography sx={{ fontSize: '11px', color: '#888', mb: '9px' }}>
            Check in to see who's here tonight
          </Typography>

          {/* Live ticker strip */}
          <TickerBar liveStats={liveStats} />

          {/* Tonight's venues — browse only, no tap */}
          <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.textDisabled, padding: '4px 0 2px' }}>
            Tonight's venues
          </Typography>
          <Typography sx={{ fontSize: '12px', color: '#555', marginBottom: '6px', marginTop: '-2px' }}>
            Tap Check In below to join a venue
          </Typography>
          <Box sx={{ maxHeight: '195px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '7px', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
            {allVenues.length === 0 ? (
              <Box sx={{ padding: '18px 0', textAlign: 'center' }}>
                <Typography sx={{ fontSize: '13px', color: C.textMuted }}>Loading venues...</Typography>
              </Box>
            ) : allVenues.map(v => (
              <Box key={v.id} onClick={() => { setSheetOpen(true); setSheetState('s1'); }} sx={{ background: C.bgCard, border: '0.5px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '10px 12px', cursor: 'pointer', transition: 'border-color 0.15s', '&:hover': { borderColor: 'rgba(13,157,165,0.3)' } }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: C.textPrimary }}>{v.name}</Typography>
                <Typography sx={{ fontSize: '11px', color: C.textDisabled, marginTop: '2px' }}>{v.area || v.city || ''}</Typography>
                <Box sx={{ display: 'flex', gap: '5px', marginTop: '6px', flexWrap: 'wrap' }}>
                  <Box sx={{ fontSize: '10px', fontWeight: 600, padding: '2px 7px', borderRadius: '20px', background: 'rgba(13,157,165,0.14)', color: C.teal, border: '0.5px solid rgba(13,157,165,0.3)' }}>
                    ● Live
                  </Box>
                  {v.has_active_offer && (
                    <Box sx={{ fontSize: '10px', fontWeight: 600, padding: '2px 7px', borderRadius: '20px', background: 'rgba(251,188,21,0.12)', color: C.yellow, border: '0.5px solid rgba(251,188,21,0.28)' }}>
                      ⚡ Offer
                    </Box>
                  )}
                  {v.active_users > 5 && (
                    <Box sx={{ fontSize: '10px', fontWeight: 600, padding: '2px 7px', borderRadius: '20px', background: 'rgba(254,114,16,0.12)', color: C.orange, border: '0.5px solid rgba(254,114,16,0.25)' }}>
                      🔥 Busy
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
          {/* Check In CTA */}
          <button
            style={{ background: C.yellow, color: C.yellowText, border: 'none', borderRadius: '12px', padding: '13px 20px', width: '100%', cursor: 'pointer', textAlign: 'center' }}
            onClick={() => { setSheetOpen(true); setSheetState('s1'); }}
          >
            <span style={{ display: 'block', fontSize: '14px', fontWeight: 700 }}>Check In</span>
            <span style={{ display: 'block', fontSize: '11px', color: 'rgba(90,58,0,0.65)', marginTop: '2px' }}>Find people around you in real-time</span>
          </button>

          {showNotifPrompt && (
            <Box sx={{ background: 'rgba(13,157,165,0.07)', border: '0.5px solid rgba(13,157,165,0.25)', borderRadius: '14px', padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <Typography sx={{ fontSize: '22px', lineHeight: 1, flexShrink: 0 }}>🔔</Typography>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: C.textPrimary, mb: 0.5 }}>Stay in the loop</Typography>
                <Typography sx={{ fontSize: '12px', color: C.textMuted, lineHeight: 1.5, mb: 1.5 }}>Get notified when someone likes you or a tournament is starting.</Typography>
                <Box sx={{ display: 'flex', gap: '8px' }}>
                  <button style={{ ...S.primaryBtn, width: 'auto', padding: '8px 14px', fontSize: '12px', borderRadius: '8px' }} onClick={() => {
                    const apiBase = process.env.REACT_APP_API_BASE || 'https://api.binko.me/api';
                    const token = localStorage.getItem('binko_token');
                    initPushNotifications(apiBase, token);
                    localStorage.setItem('binko_notif_dismissed', '1');
                    setShowNotifPrompt(false);
                  }}>Allow notifications</button>
                  <button style={{ ...S.ghostBtn, width: 'auto', padding: '8px 14px', fontSize: '12px', marginTop: 0, borderRadius: '8px' }} onClick={() => {
                    localStorage.setItem('binko_notif_dismissed', '1');
                    setShowNotifPrompt(false);
                  }}>Not now</button>
                </Box>
              </Box>
            </Box>
          )}


          {/* ── NCI CAROUSEL ── */}
          {(() => {
            const tournTime = (liveStats && liveStats.nextTournamentTime) ? liveStats.nextTournamentTime : null;
            const nciSlides = [
              {
                eyebrow: 'Live trivia',
                eyebrowColor: '#0d9da5',
                title: 'Answer questions, beat the room',
                body: 'Trivia runs live at partner venues every night. Top scorer wins a reward from the venue.',
              },
              {
                eyebrow: 'Match and meet',
                eyebrowColor: '#fbbc15',
                title: 'Like someone? Start a chat',
                body: "Binko shows who's here tonight. Like them, chat, spot them in the room.",
              },
              {
                eyebrow: 'Tournament',
                eyebrowColor: '#fe7210',
                title: tournTime ? `Tonight at ${tournTime}` : 'Compete for the top spot',
                body: tournTime
                  ? `City-wide knockout tournament at ${tournTime}. Check in at any partner venue to play.`
                  : 'City-wide knockout starts tonight. Check in at any partner venue to enter.',
              },
            ];
            return (
              <Box sx={{ marginTop: '16px', marginBottom: '4px' }}>
                <Box sx={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '0.5px solid rgba(255,255,255,0.07)',
                  borderRadius: '14px', overflow: 'hidden',
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 12px 4px' }}>
                    <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      {nciSlides.map((_, i) => (
                        <Box
                          key={i}
                          onClick={() => setNciCarouselIdx(i)}
                          sx={{ width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                        >
                          <Box sx={{
                            width: i === nciCarouselIdx ? '16px' : '5px',
                            height: '5px',
                            borderRadius: i === nciCarouselIdx ? '3px' : '50%',
                            background: i === nciCarouselIdx ? nciSlides[i].eyebrowColor : 'rgba(255,255,255,0.15)',
                            transition: 'all 0.3s',
                          }} />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box
                    onTouchStart={(e) => { nciCarouselTouchX.current = e.touches[0].clientX; }}
                    onTouchEnd={(e) => {
                      const dx = e.changedTouches[0].clientX - nciCarouselTouchX.current;
                      if (Math.abs(dx) < 40) return;
                      if (dx < 0) setNciCarouselIdx(i => Math.min(i + 1, 2));
                      else        setNciCarouselIdx(i => Math.max(i - 1, 0));
                    }}
                    sx={{ overflow: 'hidden' }}
                  >
                    <Box sx={{
                      display: 'flex',
                      transform: `translateX(-${nciCarouselIdx * 88}%)`,
                      transition: 'transform 0.38s cubic-bezier(0.4,0,0.2,1)',
                    }}>
                      {nciSlides.map((slide, i) => (
                        <Box key={i} sx={{
                          minWidth: '88%',
                          marginRight: i < 2 ? '4%' : '0',
                          padding: '10px 14px 16px',
                          userSelect: 'none',
                        }}>
                          <Typography sx={{
                            fontSize: '9px', fontWeight: 700, letterSpacing: '0.09em',
                            textTransform: 'uppercase', color: slide.eyebrowColor, mb: '5px',
                          }}>
                            {slide.eyebrow}
                          </Typography>
                          <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#e5e7eb', mb: '5px', lineHeight: 1.35 }}>
                            {slide.title}
                          </Typography>
                          <Typography sx={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.55 }}>
                            {slide.body}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })()}

          {/* ── NCI Pulse / How it works ── */}
          <Box sx={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
            <Button sx={{ ...S.ghostBtn, margin: 0, flex: 1, color: '#fbbc15' }} onClick={handleOracle}>Pulse ✨</Button>
            <Button sx={{ ...S.ghostBtn, margin: 0, flex: 1 }} onClick={() => window.open('https://www.youtube.com/watch?v=Z_Cql_83y8k', '_blank')}>How it works ▶</Button>
          </Box>

        {/* BOTTOM SHEET */}
          <Drawer
            anchor="bottom"
            open={sheetOpen}
            onClose={() => { setSheetOpen(false); setSheetState('s1'); }}
            PaperProps={{ sx: { background: C.bgCard, borderRadius: '16px 16px 0 0', padding: '0 16px 28px', maxHeight: '85vh', overflowY: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 0 8px', position: 'relative' }}
              onTouchStart={(e) => { sheetDragY.current = e.touches[0].clientY; }}
              onTouchEnd={(e) => { if (e.changedTouches[0].clientY - sheetDragY.current > 60) { setSheetOpen(false); setSheetState('s1'); } }}
            >
              <Box sx={{ width: '32px', height: '3px', background: 'rgba(255,255,255,0.15)', borderRadius: '2px' }} />
              <button onClick={() => { setSheetOpen(false); setSheetState('s1'); }} style={{ position: 'absolute', right: 0, top: '6px', width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', border: 'none', color: '#888', fontSize: '14px', cursor: 'pointer' }}>&#10005;</button>
            </Box>
            {sheetState === 's1' && (
              <Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 600, color: C.textPrimary, marginBottom: '4px' }}>Where are you checking in from?</Typography>
                <Typography sx={{ fontSize: '12px', color: C.textMuted, marginBottom: '16px' }}>We will find Binko venues near you</Typography>
                <button style={{ width: '100%', background: C.teal, border: 'none', borderRadius: '10px', padding: '13px', color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer', marginBottom: '8px' }} onClick={() => handleSheetGPS()}>Use my location</button>
                <button style={{ width: '100%', background: 'transparent', border: 'none', color: C.textMuted, fontSize: '13px', cursor: 'pointer', padding: '10px', textAlign: 'center', display: 'block' }} onClick={() => handleSheetManual()}>Enter area instead</button>
              </Box>
            )}
            {sheetState === 's2' && (
              <Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 600, color: C.textPrimary, marginBottom: '4px' }}>Finding venues near you...</Typography>
                <Typography sx={{ fontSize: '12px', color: C.textMuted, marginBottom: '16px' }}>This takes just a moment</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}><CircularProgress size={28} sx={{ color: C.teal }} /></Box>
              </Box>
            )}
            {sheetState === 's3' && (
              <Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 600, color: C.textPrimary, marginBottom: '4px' }}>Venues near you</Typography>
                <Typography sx={{ fontSize: '12px', color: C.textMuted, marginBottom: '14px' }}>Tap Check In at the venue you are at right now</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {sheetVenues.map(v => (
                    <Box key={v.id} sx={{ background: '#111', border: '0.5px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary }}>{v.name}</Typography>
                        <Typography sx={{ fontSize: '11px', color: C.textDisabled, marginTop: '1px' }}>{v.area || v.city || ''}</Typography>
                        {v._dist != null && <Typography sx={{ fontSize: '10px', color: C.teal, marginTop: '1px' }}>{(v._dist / 1000).toFixed(1)} km away</Typography>}
                        <Box sx={{ display: 'flex', gap: '4px', marginTop: '5px', flexWrap: 'wrap' }}>
                          <Box sx={{ fontSize: '9px', fontWeight: 600, padding: '2px 6px', borderRadius: '20px', background: 'rgba(13,157,165,0.14)', color: C.teal, border: '0.5px solid rgba(13,157,165,0.3)' }}>Live</Box>
                          {v.has_active_offer && <Box sx={{ fontSize: '9px', fontWeight: 600, padding: '2px 6px', borderRadius: '20px', background: 'rgba(251,188,21,0.12)', color: C.yellow, border: '0.5px solid rgba(251,188,21,0.28)' }}>Offer</Box>}
                          {v.active_users > 5 && <Box sx={{ fontSize: '9px', fontWeight: 600, padding: '2px 6px', borderRadius: '20px', background: 'rgba(254,114,16,0.12)', color: C.orange, border: '0.5px solid rgba(254,114,16,0.25)' }}>Busy</Box>}
                        </Box>
                      </Box>
                      <button style={{ background: C.yellow, border: 'none', borderRadius: '8px', padding: '8px 12px', fontSize: '11px', fontWeight: 700, color: C.yellowText, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }} onClick={() => { setSheetOpen(false); setSheetState('s1'); openCheckInModal(v); }}>Check In</button>
                    </Box>
                  ))}
                </Box>
                <button style={{ width: '100%', background: 'transparent', border: 'none', color: C.textMuted, fontSize: '12px', cursor: 'pointer', padding: '14px', textAlign: 'center', display: 'block', marginTop: '4px' }} onClick={() => handleSheetManual()}>Not at these? Enter area instead</button>
              </Box>
            )}
            {sheetState === 's4' && (
              <Box>
                <Box sx={{ textAlign: 'center', padding: '20px 10px' }}>
                  <Typography sx={{ fontSize: '24px', color: C.textDisabled, marginBottom: '10px' }}>&#128205;</Typography>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: C.textPrimary, marginBottom: '5px' }}>No Binko venues nearby</Typography>
                  <Typography sx={{ fontSize: '12px', color: C.textMuted, lineHeight: 1.5 }}>We could not find a partner venue within range. Try entering your area manually.</Typography>
                </Box>
                <button style={{ width: '100%', background: C.teal, border: 'none', borderRadius: '10px', padding: '13px', color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer', marginBottom: '8px' }} onClick={() => handleSheetManual()}>Enter area instead</button>
                <button style={{ width: '100%', background: 'transparent', border: 'none', color: C.textMuted, fontSize: '13px', cursor: 'pointer', padding: '10px', textAlign: 'center', display: 'block' }} onClick={() => { setSheetOpen(false); setSheetState('s1'); }}>Maybe later</button>
              </Box>
            )}
            {sheetState === 's5' && (
              <Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 600, color: C.textPrimary, marginBottom: '4px' }}>Select your area</Typography>
                <Typography sx={{ fontSize: '12px', color: C.textMuted, marginBottom: '14px' }}>We will show Binko venues in that neighbourhood</Typography>
                <Box sx={{ background: '#111', borderRadius: '10px', overflow: 'hidden', marginBottom: '10px' }}>
                  {Object.keys(sheetAreas).map(city => (
                    sheetAreas[city].map(a => (
                      <Box key={a.area} onClick={() => handleSheetAreaSelect(city, a.area, a.label)} sx={{ padding: '11px 13px', borderBottom: '0.5px solid rgba(255,255,255,0.06)', color: C.textPrimary, fontSize: '13px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{a.label}</span><span style={{ color: C.textDisabled }}>&#8250;</span>
                      </Box>
                    ))
                  ))}
                </Box>
                <button style={{ width: '100%', background: 'transparent', border: 'none', color: C.textMuted, fontSize: '13px', cursor: 'pointer', padding: '10px', textAlign: 'center', display: 'block' }} onClick={() => setSheetState('s1')}>Back</button>
              </Box>
            )}
            {sheetState === 's6' && (
              <Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 600, color: C.textPrimary, marginBottom: '4px' }}>Venues in {sheetSelectedArea}</Typography>
                <Typography sx={{ fontSize: '12px', color: C.textMuted, marginBottom: '14px' }}>Tap Check In at the venue you are at right now</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {sheetVenues.length === 0 ? (
                    <Box sx={{ textAlign: 'center', padding: '20px 0' }}><Typography sx={{ fontSize: '13px', color: C.textMuted }}>No venues found in this area.</Typography></Box>
                  ) : sheetVenues.map(v => (
                    <Box key={v.id} sx={{ background: '#111', border: '0.5px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary }}>{v.name}</Typography>
                        <Typography sx={{ fontSize: '11px', color: C.textDisabled, marginTop: '1px' }}>{v.area || v.city || ''}</Typography>
                        <Box sx={{ display: 'flex', gap: '4px', marginTop: '5px', flexWrap: 'wrap' }}>
                          <Box sx={{ fontSize: '9px', fontWeight: 600, padding: '2px 6px', borderRadius: '20px', background: 'rgba(13,157,165,0.14)', color: C.teal, border: '0.5px solid rgba(13,157,165,0.3)' }}>Live</Box>
                          {v.has_active_offer && <Box sx={{ fontSize: '9px', fontWeight: 600, padding: '2px 6px', borderRadius: '20px', background: 'rgba(251,188,21,0.12)', color: C.yellow, border: '0.5px solid rgba(251,188,21,0.28)' }}>Offer</Box>}
                          {v.active_users > 5 && <Box sx={{ fontSize: '9px', fontWeight: 600, padding: '2px 6px', borderRadius: '20px', background: 'rgba(254,114,16,0.12)', color: C.orange, border: '0.5px solid rgba(254,114,16,0.25)' }}>Busy</Box>}
                        </Box>
                      </Box>
                      <button style={{ background: C.yellow, border: 'none', borderRadius: '8px', padding: '8px 12px', fontSize: '11px', fontWeight: 700, color: C.yellowText, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }} onClick={() => { setSheetOpen(false); setSheetState('s1'); openCheckInModal(v); }}>Check In</button>
                    </Box>
                  ))}
                </Box>
                <button style={{ width: '100%', background: 'transparent', border: 'none', color: C.textMuted, fontSize: '13px', cursor: 'pointer', padding: '14px', textAlign: 'center', display: 'block', marginTop: '4px' }} onClick={() => setSheetState('s5')}>Change area</button>
              </Box>
            )}
          </Drawer>
        </>
      )}


      {/* Love Oracle Modal — outside ternary, works in both states */}
      <Modal open={oracleOpen} onClose={() => setOracleOpen(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '85%', maxWidth: 360,
          background: C.bgCard,
          border: `0.5px solid rgba(251,188,21,0.25)`,
          borderRadius: '16px', p: 3,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
          textAlign: 'center',
        }}>
          <Typography sx={{ fontSize: '28px', lineHeight: 1 }}>✨</Typography>
          <Typography sx={{ fontSize: '10px', color: C.yellow, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 500 }}>
            Today's Love Oracle
          </Typography>
          <Typography sx={{ fontSize: '15px', color: C.textPrimary, lineHeight: 1.65, fontStyle: 'italic' }}>
            "{oracleText}"
          </Typography>
          <Typography sx={{ fontSize: '11px', color: C.textDisabled }}>Changes at midnight</Typography>
          <Button onClick={() => setOracleOpen(false)} sx={{
            ...S.ghostBtn, margin: 0, width: '100%',
            color: C.yellow, border: `0.5px solid rgba(251,188,21,0.3)`,
            borderRadius: '20px',
          }}>Close</Button>
        </Box>
      </Modal>

      {/* ── CHECK-IN MODAL (bottom sheet style) ── */}
      <Modal open={modalOpen} onClose={() => !submitting && setModalOpen(false)}>
        <Box sx={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: C.bgCard, borderRadius: '20px 20px 0 0',
          border: '0.5px solid rgba(255,255,255,0.1)',
          padding: '20px 20px 32px', maxHeight: '85vh', overflowY: 'auto'
        }}>
          <Box sx={{ width: '40px', height: '4px', background: 'rgba(255,255,255,0.15)', borderRadius: '2px', margin: '0 auto 20px' }} />
          <Typography sx={{ fontSize: '17px', fontWeight: 500, color: C.textPrimary, marginBottom: '4px' }}>
            Check into {modalVenue?.name}
          </Typography>

          {isFirstTime && (
            <Stepper activeStep={modalStep} sx={{ marginBottom: '20px', '& .MuiStepLabel-label': { color: C.textMuted, fontSize: '11px' }, '& .MuiStepLabel-label.Mui-active': { color: C.yellow } }}>
              <Step><StepLabel>Your look</StepLabel></Step>
              <Step><StepLabel>Tonight</StepLabel></Step>
              <Step><StepLabel>Confirm</StepLabel></Step>
            </Stepper>
          )}

          {modalError && (
            <Typography sx={{ fontSize: '13px', color: C.orange, marginBottom: '12px', background: 'rgba(255,68,96,0.1)', padding: '8px 12px', borderRadius: '8px' }}>
              {modalError}
            </Typography>
          )}

          {/* Step 0 — Profile identifiers (first time only) */}
          {modalStep === 0 && isFirstTime && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Typography sx={{ fontSize: '13px', color: C.textMuted }}>This helps people spot you. Saved to your profile.</Typography>
              <FormControl>
                <FormLabel sx={{ fontSize: '13px', color: C.textMuted, marginBottom: '8px' }}>Hair</FormLabel>
                <RadioGroup value={hairType} onChange={e => setHairType(e.target.value)}>
                  {['Long','Short','Bald','Tied up'].map(v => (
                    <FormControlLabel key={v} value={v} control={<Radio size="small" sx={{ color: C.textDisabled, '&.Mui-checked': { color: C.yellow } }} />} label={<Typography sx={{ fontSize: '13px', color: C.textPrimary }}>{v}</Typography>} />
                  ))}
                </RadioGroup>
              </FormControl>
              <FormControl>
                <FormLabel sx={{ fontSize: '13px', color: C.textMuted, marginBottom: '8px' }}>Facial hair</FormLabel>
                <RadioGroup value={facialHair} onChange={e => setFacialHair(e.target.value)}>
                  {['None','Beard','Mustache','Both'].map(v => (
                    <FormControlLabel key={v} value={v} control={<Radio size="small" sx={{ color: C.textDisabled, '&.Mui-checked': { color: C.yellow } }} />} label={<Typography sx={{ fontSize: '13px', color: C.textPrimary }}>{v}</Typography>} />
                  ))}
                </RadioGroup>
              </FormControl>
              <FormControlLabel control={<Checkbox checked={hasGlasses} onChange={e => setHasGlasses(e.target.checked)} size="small" sx={{ color: C.textDisabled, '&.Mui-checked': { color: C.yellow } }} />} label={<Typography sx={{ fontSize: '13px', color: C.textPrimary }}>I usually wear glasses</Typography>} />
              <button style={{ ...S.primaryBtn, opacity: hairType ? 1 : 0.5 }} disabled={!hairType} onClick={() => setModalStep(1)}>Next →</button>
            </Box>
          )}

          {/* Step 1 — Session identifiers */}
          {modalStep === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Typography sx={{ fontSize: '13px', color: C.textMuted }}>Help others find you tonight.</Typography>
              {hasGlasses && (
                <FormControlLabel control={<Checkbox checked={wearingGlasses} onChange={e => setWearingGlasses(e.target.checked)} size="small" sx={{ color: C.textDisabled, '&.Mui-checked': { color: C.yellow } }} />} label={<Typography sx={{ fontSize: '13px', color: C.textPrimary }}>Wearing glasses right now</Typography>} />
              )}
              <FormControl>
                <FormLabel sx={{ fontSize: '13px', color: C.textMuted, marginBottom: '8px' }}>Where in the venue?</FormLabel>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {['Bar','Tables','Lounge','Entrance','Outdoor','Standing'].map(v => (
                    <Chip key={v} label={v} size="small" onClick={() => setLocationArea(v)}
                      sx={{ fontSize: '12px', cursor: 'pointer', background: locationArea === v ? 'rgba(251,188,21,0.15)' : 'rgba(255,255,255,0.05)', color: locationArea === v ? C.yellow : C.textMuted, border: locationArea === v ? `0.5px solid ${C.yellow}` : '0.5px solid rgba(255,255,255,0.07)' }} />
                  ))}
                </Box>
              </FormControl>
              <FormControl>
                <FormLabel sx={{ fontSize: '13px', color: C.textMuted, marginBottom: '8px' }}>Clothing (pick up to 2)</FormLabel>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {[['Dark top','Light top','Bright colors'],['Dress','Suit','Hoodie','Jacket']].map((row, ri) => (
                    <Box key={ri} sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {row.map(v => {
                        const selected = clothingPrimary === v || clothingSecondary === v;
                        return (
                          <Chip key={v} label={v} size="small" onClick={() => {
                            if (clothingPrimary === v) { setClothingPrimary(''); return; }
                            if (clothingSecondary === v) { setClothingSecondary(''); return; }
                            if (!clothingPrimary) { setClothingPrimary(v); return; }
                            if (!clothingSecondary) { setClothingSecondary(v); return; }
                          }}
                            sx={{ fontSize: '12px', cursor: 'pointer', background: selected ? 'rgba(251,188,21,0.15)' : 'rgba(255,255,255,0.05)', color: selected ? C.yellow : C.textMuted, border: selected ? `0.5px solid ${C.yellow}` : '0.5px solid rgba(255,255,255,0.07)' }} />
                        );
                      })}
                    </Box>
                  ))}
                </Box>
              </FormControl>
              <FormControl>
                <FormLabel sx={{ fontSize: '13px', color: C.textMuted, marginBottom: '8px' }}>Accessories</FormLabel>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {['Cap','Backpack','Handbag','Nothing noticeable'].map(v => (
                    <Chip key={v} label={v} size="small" onClick={() => toggleAccessory(v)}
                      sx={{ fontSize: '12px', cursor: 'pointer', background: accessories.includes(v) ? 'rgba(251,188,21,0.15)' : 'rgba(255,255,255,0.05)', color: accessories.includes(v) ? C.yellow : C.textMuted, border: accessories.includes(v) ? `0.5px solid ${C.yellow}` : '0.5px solid rgba(255,255,255,0.07)' }} />
                  ))}
                </Box>
              </FormControl>
              <FormControl>
                <FormLabel sx={{ fontSize: '13px', color: C.textMuted, marginBottom: '8px' }}>Group size</FormLabel>
                <RadioGroup value={groupStatus} onChange={e => setGroupStatus(e.target.value)}>
                  {['Solo','With 1-2 friends','With a group'].map(v => (
                    <FormControlLabel key={v} value={v} control={<Radio size="small" sx={{ color: C.textDisabled, '&.Mui-checked': { color: C.yellow } }} />} label={<Typography sx={{ fontSize: '13px', color: C.textPrimary }}>{v}</Typography>} />
                  ))}
                </RadioGroup>
              </FormControl>
              <Box sx={{ display: 'flex', gap: '8px' }}>
                {isFirstTime && <button style={{ ...S.ghostBtn, width: 'auto', padding: '10px 16px' }} onClick={() => setModalStep(0)}>← Back</button>}
                <button style={{ ...S.primaryBtn, opacity: locationArea && groupStatus ? 1 : 0.5 }} disabled={!locationArea || !groupStatus} onClick={() => setModalStep(2)}>Next →</button>
              </Box>
            </Box>
          )}

          {/* Step 2 — Confirm */}
          {modalStep === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Typography sx={{ fontSize: '13px', color: C.textMuted }}>This is how others will see you:</Typography>
              <Box sx={{ background: C.bgApp, borderRadius: '12px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: C.textPrimary }}>{user?.alias} · {ageBand}</Typography>
                {locationArea && <Typography sx={{ fontSize: '12px', color: C.textMuted }}>📍 {locationArea}</Typography>}
                {(clothingPrimary || clothingSecondary) && <Typography sx={{ fontSize: '12px', color: C.textMuted }}>👕 {[clothingPrimary, clothingSecondary].filter(Boolean).join(', ')}</Typography>}
                {user?.hair_type && <Typography sx={{ fontSize: '12px', color: C.textMuted }}>✨ {user.hair_type}{user.facial_hair && user.facial_hair !== 'None' ? `, ${user.facial_hair}` : ''}{wearingGlasses ? ', glasses' : ''}</Typography>}
                {accessories.length > 0 && <Typography sx={{ fontSize: '12px', color: C.textMuted }}>🎒 {accessories.join(', ')}</Typography>}
                {groupStatus && <Typography sx={{ fontSize: '12px', color: C.textMuted }}>👥 {groupStatus}</Typography>}
              </Box>
              <Box sx={{ display: 'flex', gap: '8px' }}>
                <button style={{ ...S.ghostBtn, width: 'auto', padding: '10px 16px' }} onClick={() => setModalStep(1)}>← Back</button>
                <button style={{ ...S.primaryBtn, opacity: submitting ? 0.6 : 1 }} disabled={submitting} onClick={submitCheckIn}>
                  {submitting ? 'Checking in...' : 'Check In ✓'}
                </button>
              </Box>
            </Box>
          )}
        </Box>
      </Modal>

      {/* App feedback strip — bottom of page, above nav */}
      {showFeedbackStrip && (
        <Box sx={{
          background: 'rgba(13,157,165,0.08)',
          border: '0.5px solid rgba(13,157,165,0.25)',
          borderRadius: '12px',
          padding: '12px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          mt: '4px',
        }}>
          <Typography sx={{ fontSize: '13px', color: C.textPrimary, flex: 1 }}>
            Enjoying Binko? Let us know
          </Typography>
          <Box sx={{ display: 'flex', gap: '10px' }}>
            <Box
              onClick={() => { setFbTapped(true); setFbRating('positive'); setFbDone(false); setFbDrawerOpen(true); }}
              sx={{ fontSize: '22px', cursor: 'pointer', lineHeight: 1, userSelect: 'none' }}
            >👍</Box>
            <Box
              onClick={() => { setFbTapped(true); setFbRating('negative'); setFbDone(false); setFbDrawerOpen(true); }}
              sx={{ fontSize: '22px', cursor: 'pointer', lineHeight: 1, userSelect: 'none' }}
            >👎</Box>
          </Box>
        </Box>
      )}

      {/* App feedback drawer */}
      <Drawer
        anchor="bottom"
        open={fbDrawerOpen}
        onClose={handleFbSkip}
        PaperProps={{
          sx: {
            background: C.bgCard,
            border: '0.5px solid rgba(255,255,255,0.1)',
            borderRadius: '20px 20px 0 0',
            padding: '20px 20px 40px',
          }
        }}
      >
        <Box sx={{ width: '40px', height: '4px', background: 'rgba(255,255,255,0.15)', borderRadius: '2px', margin: '0 auto 20px' }} />
        {fbDone ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography sx={{ fontSize: '24px', mb: 1 }}>🙏</Typography>
            <Typography sx={{ fontSize: '15px', fontWeight: 600, color: C.textPrimary }}>Thanks for the feedback!</Typography>
          </Box>
        ) : fbRating === 'positive' ? (
          <AppFbPositive onSubmit={handleFbSubmit} onSkip={handleFbSkip} />
        ) : (
          <AppFbNegative onSubmit={handleFbSubmit} onSkip={handleFbSkip} />
        )}
      </Drawer>

    </Box>
  );
}